import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Compass, Briefcase } from 'lucide-react';
import { GlobalHeader } from '../components/GlobalHeader';
import { Footer } from '../components/Footer';
import { SelectedCases } from '../components/SelectedCases';
import SEO from '../components/SEO';
import letsTalkImg from '../assets/lets-talk-image.jpg';

const PILLAR_CARDS = [
  {
    icon: <Briefcase size={36} strokeWidth={1.5} />,
    title: 'Offerings',
    eyebrow: 'What you can buy',
    description: 'A Fractional Head of Data & AI - strategy, feasibility, PoC, productionisation and adoption. Engage the whole thing or any one piece.',
    href: '/offerings',
    cta: 'See offerings',
    testid: 'home-pillar-offerings',
    accent: 'linear-gradient(135deg, rgba(126, 179, 213, 0.18) 0%, rgba(58, 80, 107, 0.04) 100%)',
    accentRing: 'rgba(126, 179, 213, 0.3)',
  },
  {
    icon: <Compass size={36} strokeWidth={1.5} />,
    title: 'Capabilities',
    eyebrow: 'How we do it',
    description: 'Aim it. Build it. Make it stick. The full continuum: strategy that names the few right bets, engineering that ships into production, and adoption that gets it used.',
    href: '/capabilities',
    cta: 'See capabilities',
    testid: 'home-pillar-capabilities',
    accent: 'linear-gradient(135deg, rgba(58, 80, 107, 0.16) 0%, rgba(14, 30, 47, 0.02) 100%)',
    accentRing: 'rgba(58, 80, 107, 0.3)',
  },
];

// Trust strip - 3 columns on WHITE bg.
// Only using logo files with verified proper transparency; the rest are text chips.
const TRUST_CAPABILITIES_LOGOS = [
  // Cloud & data platforms
  { src: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/azure/azure-original.svg', alt: 'Microsoft Azure' },
  { src: 'https://cdn.simpleicons.org/googlecloud', alt: 'Google Cloud' },
  { src: 'https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/98qyzixh_image.png', alt: 'AWS' },
  { src: 'https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/njwom4or_image.png', alt: 'Databricks' },
  // AI & models
  { src: 'https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/cug63ism_image.png', alt: 'OpenAI' },
  { src: 'https://cdn.simpleicons.org/anthropic', alt: 'Anthropic' },
  { src: 'https://cdn.simpleicons.org/huggingface', alt: 'Hugging Face' },
  { src: 'https://cdn.simpleicons.org/langchain', alt: 'LangChain' },
  // ML & data
  { src: 'https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/x6l6n46j_image.png', alt: 'PyTorch' },
  { src: 'https://cdn.simpleicons.org/tensorflow', alt: 'TensorFlow' },
  { src: 'https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/nnn0wvtp_image.png', alt: 'Snowflake' },
  { src: 'https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/qcjqupzy_image.png', alt: 'dbt' },
  // BI & orchestration
  { src: 'https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/a6swqhhr_image.png', alt: 'Power BI' },
  { src: 'https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/q85h09ez_image.png', alt: 'Apache Airflow' },
  { src: 'https://cdn.simpleicons.org/streamlit', alt: 'Streamlit' },
  { src: 'https://cdn.simpleicons.org/sap', alt: 'SAP' },
  // Enterprise & OEM industrial
  { src: 'https://cdn.simpleicons.org/siemens', alt: 'Siemens' },
  { src: 'https://cdn.simpleicons.org/abb', alt: 'ABB' },
  { src: 'https://cdn.simpleicons.org/schneiderelectric', alt: 'Schneider Electric' },
  { src: 'https://cdn.simpleicons.org/mitsubishi', alt: 'Mitsubishi Electric' },
  // Heavy machinery & equipment
  { src: 'https://cdn.simpleicons.org/caterpillar', alt: 'Caterpillar' },
  { src: 'https://cdn.simpleicons.org/volvo', alt: 'Volvo' },
  { src: 'https://cdn.simpleicons.org/johndeere', alt: 'John Deere' },
];

const TRUST_BACKGROUNDS_LOGOS = [
  { src: '/company-logos/logo-26.png', alt: 'Netcompany', invert: true },
  { src: '/company-logos/logo-25.png', alt: 'Implement', invert: true },
  { src: '/company-logos/logo-24.png', alt: 'KPMG', invert: true },
  { src: '/company-logos/real/accenture.png', alt: 'Accenture' },
  { src: '/company-logos/real/pwc.png', alt: 'PwC' },
];

const TRUST_CLIENTS_LOGOS = [
  { src: '/company-logos/real/flsmidth.png', alt: 'FLSmidth' },
  { src: '/company-logos/real/pjm.svg', alt: 'PJM A/S' },
  { src: '/company-logos/real/ntec.png', alt: 'NTEC - Navajo Transitional Energy Company' },
  { src: '/company-logos/real/bollerup-jensen.svg', alt: 'Bollerup Jensen' },
  { monogram: 'Navajo Mine', alt: 'Navajo Mine', wide: true },
  { monogram: 'Spring Creek Mine', alt: 'Spring Creek Mine', wide: true },
];

export const HomePage = () => {
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Aplied.ai",
    "url": "https://aplied.ai",
    "logo": "https://aplied.ai/logo.png",
    "description": "AI consultancy and engineering services for heavy industry - mining, cement, energy, oil & gas, manufacturing, OEM and construction.",
    "foundingDate": "2020",
    "address": { "@type": "PostalAddress", "addressLocality": "Copenhagen", "addressCountry": "DK" },
    "contactPoint": { "@type": "ContactPoint", "contactType": "Business", "url": "https://aplied.ai/book-a-meeting" },
    "sameAs": ["https://www.linkedin.com/company/aplied-ai"],
    "knowsAbout": ["Artificial Intelligence", "Machine Learning", "Mining Operations", "Industrial Automation", "Predictive Maintenance", "Process Optimization"],
  };

  useEffect(() => {
    const sections = document.querySelectorAll('.fade-in-section');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '50px' });
    sections.forEach(section => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="blended-page mining-page">
      <SEO
        title="Aplied.ai | AI Consultancy for Heavy Industry"
        description="We turn heavy industry's existing data into profitable AI solutions. Mining, cement, energy, oil & gas, manufacturing, OEM and construction. Based in Copenhagen, working globally."
        canonical="/"
        schema={organizationSchema}
      />
      <GlobalHeader />

      {/* Hero - broadened title */}
      <section id="main-content" className="blended-hero">
        <div className="hero-video-background">
          <video autoPlay loop muted playsInline className="hero-video">
            <source src="/assets/homepage-hero-video.mp4" type="video/mp4" />
          </video>
          <div className="hero-video-overlay" />
        </div>
        <div className="hero-container">
          <h1 className="blended-hero-title">
            <span className="hero-title-desktop">We turn heavy industry&apos;s existing data<br />into profitable AI solutions.</span>
            <span className="hero-title-mobile">We turn heavy industry&apos;s existing data into profitable AI solutions.</span>
          </h1>
          <p className="hero-location">Based in Copenhagen, Denmark, working globally.</p>
          <p className="hero-stats">
            Consultants with 25+ years of combined AI experience | 80+ projects | Work delivered in 20+ countries.
          </p>
        </div>

        {/* Logo Slider at bottom of hero */}
        <div className="hero-logos" aria-label="Client and partner logos">
          <div className="hero-container">
            <div className="client-strip">
              <div className="client-strip__inner">
                <img src="/company-logos/logo-24.png" alt="Implement" className="client-logo client-logo--large" />
                <img src="/company-logos/logo-25.png" alt="Netcompany" className="client-logo client-logo--large" />
                <img src="/company-logos/logo-26.png" alt="Accenture" className="client-logo client-logo--large" />
                <img src="/company-logos/logo-27.png" alt="Codeex" className="client-logo client-logo--large" />
                <img src="/company-logos/logo-28.png" alt="Codellent" className="client-logo client-logo--large" />
                <img src="https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/cug63ism_image.png" alt="OpenAI" className="client-logo client-logo--small" />
                <img src="https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/98qyzixh_image.png" alt="AWS" className="client-logo client-logo--small" />
                <img src="https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/e6x6jfsy_image.png" alt="Hugging Face" className="client-logo" />
                <img src="https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/99dj749p_image.png" alt="Vercel" className="client-logo" />
                <img src="https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/c1h4a5gb_image.png" alt="Wirtgen" className="client-logo" />
                <img src="https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/x6l6n46j_image.png" alt="PyTorch" className="client-logo client-logo--small" />
                <img src="https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/h80tgrdj_image.png" alt="AWS Logo" className="client-logo client-logo--small" />
                <img src="https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/nnn0wvtp_image.png" alt="Snowflake" className="client-logo" />
                <img src="https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/qcjqupzy_image.png" alt="Hugging Face Logo" className="client-logo" />
                <img src="https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/q85h09ez_image.png" alt="Apache Airflow" className="client-logo" />
                <img src="https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/u7ljz682_image.png" alt="MLflow" className="client-logo" />
                <img src="https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/a6swqhhr_image.png" alt="Power BI" className="client-logo" />
                <img src="https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/80gls4d8_image.png" alt="Streamlit" className="client-logo" />
                <img src="https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/njwom4or_image.png" alt="Databricks" className="client-logo" />

                <img src="/company-logos/logo-24.png" alt="Implement" className="client-logo client-logo--large" />
                <img src="/company-logos/logo-25.png" alt="Netcompany" className="client-logo client-logo--large" />
                <img src="/company-logos/logo-26.png" alt="Accenture" className="client-logo client-logo--large" />
                <img src="/company-logos/logo-27.png" alt="Codeex" className="client-logo client-logo--large" />
                <img src="/company-logos/logo-28.png" alt="Codellent" className="client-logo client-logo--large" />
                <img src="https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/cug63ism_image.png" alt="OpenAI" className="client-logo client-logo--small" />
                <img src="https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/98qyzixh_image.png" alt="AWS" className="client-logo client-logo--small" />
                <img src="https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/e6x6jfsy_image.png" alt="Hugging Face" className="client-logo" />
                <img src="https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/99dj749p_image.png" alt="Vercel" className="client-logo" />
                <img src="https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/c1h4a5gb_image.png" alt="Wirtgen" className="client-logo" />
                <img src="https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/x6l6n46j_image.png" alt="PyTorch" className="client-logo client-logo--small" />
                <img src="https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/h80tgrdj_image.png" alt="AWS Logo" className="client-logo client-logo--small" />
                <img src="https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/nnn0wvtp_image.png" alt="Snowflake" className="client-logo" />
                <img src="https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/qcjqupzy_image.png" alt="Hugging Face Logo" className="client-logo" />
                <img src="https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/q85h09ez_image.png" alt="Apache Airflow" className="client-logo" />
                <img src="https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/u7ljz682_image.png" alt="MLflow" className="client-logo" />
                <img src="https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/a6swqhhr_image.png" alt="Power BI" className="client-logo" />
                <img src="https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/80gls4d8_image.png" alt="Streamlit" className="client-logo" />
                <img src="https://customer-assets.emergentagent.com/job_65da4d38-3aef-429d-87af-fdb75a8d2d78/artifacts/njwom4or_image.png" alt="Databricks" className="client-logo" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What we do - 2 visually-rich pillar cards */}
      <section className="mining-what-we-do fade-in-section">
        <div className="section-content">
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <span style={{
              display: 'inline-block', fontSize: 13, fontWeight: 600, letterSpacing: '0.12em',
              textTransform: 'uppercase', color: 'var(--strategy-steel-blue)', marginBottom: 12, opacity: 0.85,
            }}>
              What we do
            </span>
            <h2 className="section-main-title" style={{ marginBottom: 16 }}>Two ways into the work</h2>
            <p style={{
              fontSize: 18, lineHeight: 1.6, color: 'var(--strategy-navy)',
              maxWidth: 820, margin: '0 auto', opacity: 0.85,
            }}>
              Start with what you can buy or how we do it. Either path leads to the same conversation.
            </p>
          </div>

          <div className="home-pillar-grid">
            {PILLAR_CARDS.map((p) => (
              <Link
                key={p.title}
                to={p.href}
                className="home-pillar-card-v2"
                data-testid={p.testid}
                style={{
                  position: 'relative',
                  display: 'block',
                  padding: '56px 56px 44px',
                  background: '#FFFFFF',
                  border: '1px solid rgba(58,80,107,0.1)',
                  borderRadius: 20,
                  textDecoration: 'none',
                  color: 'inherit',
                  overflow: 'hidden',
                  boxShadow: '0 4px 20px rgba(20,40,56,0.05)',
                  transition: 'all 400ms cubic-bezier(0.4, 0, 0.2, 1)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-6px)';
                  e.currentTarget.style.boxShadow = '0 24px 48px rgba(58,80,107,0.12)';
                  e.currentTarget.style.borderColor = p.accentRing;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(20,40,56,0.05)';
                  e.currentTarget.style.borderColor = 'rgba(58,80,107,0.1)';
                }}
              >
                {/* decorative accent in top-right */}
                <div aria-hidden="true" style={{
                  position: 'absolute', top: 0, right: 0,
                  width: 280, height: 280,
                  background: p.accent,
                  borderRadius: '0 20px 0 100%',
                  pointerEvents: 'none',
                }}></div>

                <div style={{ position: 'relative', zIndex: 1 }}>
                  {/* eyebrow */}
                  <span style={{
                    display: 'block', fontSize: 12, fontWeight: 700,
                    letterSpacing: '0.14em', textTransform: 'uppercase',
                    color: 'var(--strategy-steel-blue)', marginBottom: 28, opacity: 0.75,
                  }}>
                    {p.eyebrow}
                  </span>

                  {/* icon */}
                  <div style={{
                    width: 76, height: 76, borderRadius: 18,
                    background: 'linear-gradient(135deg, var(--strategy-deep-navy) 0%, var(--strategy-steel-blue) 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#FFFFFF', marginBottom: 32,
                    boxShadow: '0 10px 28px rgba(58,80,107,0.28)',
                  }}>
                    {p.icon}
                  </div>

                  <h3 style={{
                    fontFamily: "'Inter Tight','IBM Plex Sans','Inter',sans-serif",
                    fontSize: 36, fontWeight: 600, lineHeight: 1.1,
                    letterSpacing: '-0.03em', color: 'var(--strategy-navy)',
                    margin: '0 0 18px',
                  }}>
                    {p.title}
                  </h3>

                  <p style={{
                    fontSize: 17, lineHeight: 1.6, color: 'var(--strategy-navy)',
                    opacity: 0.82, margin: '0 0 36px', maxWidth: 480,
                  }}>
                    {p.description}
                  </p>

                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 10,
                    padding: '12px 22px', borderRadius: 999,
                    background: 'var(--strategy-deep-navy)', color: '#FFFFFF',
                    fontSize: 14, fontWeight: 600, letterSpacing: '-0.005em',
                    boxShadow: '0 4px 16px rgba(14,30,47,0.18)',
                  }}>
                    {p.cta} <ArrowRight size={16} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Trust strip - 3 columns of logos on WHITE bg */}
      <section className="fade-in-section trust-strip-section" data-testid="trust-strip">
        <div className="section-content">
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <span style={{
              display: 'inline-block', fontSize: 13, fontWeight: 600, letterSpacing: '0.12em',
              textTransform: 'uppercase', color: 'var(--strategy-steel-blue)', marginBottom: 12, opacity: 0.9,
            }}>
              The foundations
            </span>
            <h2 className="section-main-title" style={{ marginBottom: 16 }}>
              Built on what we know
            </h2>
            <p style={{
              fontSize: 18, lineHeight: 1.6, color: 'var(--strategy-navy)',
              maxWidth: 720, margin: '0 auto', opacity: 0.85,
            }}>
              The platforms we ship on, the firms we came from, and the operators we&apos;ve served.
            </p>
          </div>

          <div className="trust-strip-grid">
            <div className="trust-strip-col trust-strip-col--wide" data-testid="trust-col-capabilities">
              <h3 className="trust-strip-heading">Capabilities</h3>
              <div className="trust-logo-grid trust-logo-grid--4col">
                {TRUST_CAPABILITIES_LOGOS.map((logo, i) => (
                  <div key={logo.alt} className="trust-logo-cell" title={logo.alt}>
                    <img
                      src={logo.src}
                      alt={logo.alt}
                      loading="lazy"
                      onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="trust-strip-col" data-testid="trust-col-backgrounds">
              <h3 className="trust-strip-heading">Backgrounds &amp; experiences</h3>
              <div className="trust-logo-grid trust-logo-grid--2col">
                {TRUST_BACKGROUNDS_LOGOS.map((item) => (
                  item.src ? (
                    <div key={item.alt} className={`trust-logo-cell trust-logo-cell--lg${item.invert ? ' trust-logo-cell--invert' : ''}`} title={item.alt}>
                      <img
                        src={item.src}
                        alt={item.alt}
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                      />
                    </div>
                  ) : (
                    <div key={item.alt} className={`trust-logo-cell trust-logo-cell--lg trust-logo-cell--monogram${item.wide ? ' trust-logo-cell--wide' : ''}`} title={item.alt}>
                      <span className="trust-monogram">{item.monogram}</span>
                    </div>
                  )
                ))}
              </div>
            </div>

            <div className="trust-strip-col" data-testid="trust-col-clients">
              <h3 className="trust-strip-heading">Clients</h3>
              <div className="trust-logo-grid trust-logo-grid--2col">
                {TRUST_CLIENTS_LOGOS.map((item) => (
                  item.src ? (
                    <div key={item.alt} className="trust-logo-cell trust-logo-cell--lg" title={item.alt}>
                      <img
                        src={item.src}
                        alt={item.alt}
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                      />
                    </div>
                  ) : (
                    <div key={item.alt} className={`trust-logo-cell trust-logo-cell--lg trust-logo-cell--monogram${item.wide ? ' trust-logo-cell--wide' : ''}`} title={item.alt}>
                      <span className="trust-monogram">{item.monogram}</span>
                    </div>
                  )
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Selected Cases carousel */}
      <SelectedCases vertical="mining" />

      {/* Let's talk - dark CTA */}
      <section id="book-meeting-cta" className="mining-lets-talk fade-in-section">
        <div className="mining-lets-talk-overlay"></div>
        <div className="section-content">
          <div className="mining-lets-talk-layout">
            <div className="mining-lets-talk-left">
              <h2 className="section-main-title-light">Bring us the asset you worry about most.</h2>

              <div className="mining-lets-talk-image-mobile">
                <img src={letsTalkImg} alt="Consultation Meeting" loading="lazy" />
              </div>

              <p className="mining-lets-talk-intro">That is usually where the clearest, fastest-paying use case is hiding.</p>
              <p className="mining-lets-talk-description">
                We&apos;re a team of AI consultants and engineers focused on heavy industry. Tell us
                what you run and where it hurts - we&apos;ll tell you the smallest first move that pays.
              </p>

              <Link
                to="/book-a-meeting"
                className="mining-show-form-btn"
                data-testid="home-book-meeting-cta"
              >
                Book a meeting
                <ArrowRight size={20} />
              </Link>
            </div>

            <div className="mining-lets-talk-image-container">
              <img src={letsTalkImg} alt="Consultation Meeting" />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
